/**
 * Installment Wise Table Component
 * Displays fee data grouped by installment/payment term with proper RowSpan
 */
import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';
import { FeeFilter } from './sharedComponents';

/**
 * Installment Wise Table Component with API data using proper RowSpan
 * @param {Array} rawApiData - Raw API data from get-feetype-terms
 * @param {boolean} isLoading - Loading state
 * @returns {JSX.Element} - Installment Wise table component
 */
export const InstallmentWiseTable = ({ rawApiData, isLoading }) => {
    const [feeFilter, setFeeFilter] = React.useState('all'); // 'all', 'regular', 'misc'

    if (isLoading) {
        return <LoadingComponent />;
    }

    // Always show table structure, even with empty data
    const hasData = rawApiData && Array.isArray(rawApiData) && rawApiData.length > 0;

    // Filter data based on selected filter
    let filteredData = hasData ? rawApiData : [];
    if (feeFilter === 'regular') {
        filteredData = filteredData.filter(item => item.isPlanExists === true);
    } else if (feeFilter === 'misc') {
        filteredData = filteredData.filter(item => item.isPlanExists === false);
    }

    // Group data by className and paymentTerm (installment)
    const groupedData = {};
    let sNoCounter = 1;

    filteredData.forEach(item => {
        const className = item.className || 'Unknown Class';
        const paymentTerm = item.paymentTerm || 'Unknown';
        const key = `${className}_${paymentTerm}`;

        if (!groupedData[key]) {
            groupedData[key] = {
                sNo: sNoCounter++,
                className: className,
                paymentTerm: paymentTerm,
                fees: []
            };
        }

        groupedData[key].fees.push({
            feeType: item.feeType || '',
            termWiseActualValue: item.termWiseActualValue || 0,
            concession: item.concession || 0,
            netAmount: (item.termWiseActualValue || 0) - (item.concession || 0),
            amountPaid: item.amountPaid || 0,
            dueAmount: item.dueAmount || 0,
            dueDate: item.dueDate || ''
        });
    });

    // Convert grouped data to table format with rowSpan
    const tableData = [];
    const groupArray = Object.values(groupedData);

    // Sort groupArray: "One Time" first, then "Installment 1", "Installment 2", etc.
    groupArray.sort((a, b) => {
        const aIsOneTime = a.paymentTerm.toLowerCase().includes('one time');
        const bIsOneTime = b.paymentTerm.toLowerCase().includes('one time');

        if (aIsOneTime && bIsOneTime) return 0;
        if (aIsOneTime) return -1;
        if (bIsOneTime) return 1;

        const aMatch = a.paymentTerm.match(/\d+/);
        const bMatch = b.paymentTerm.match(/\d+/);

        if (aMatch && bMatch) {
            return parseInt(aMatch[0]) - parseInt(bMatch[0]);
        }

        return a.paymentTerm.localeCompare(b.paymentTerm);
    });

    groupArray.forEach((group) => {
        const feesCount = group.fees.length;

        group.fees.forEach((fee, index) => {
            tableData.push({
                sNo: group.sNo,
                paymentTerm: group.paymentTerm,
                feeType: fee.feeType,
                termWiseActualValue: fee.termWiseActualValue,
                concession: fee.concession,
                netAmount: fee.netAmount,
                amountPaid: fee.amountPaid,
                dueAmount: fee.dueAmount,
                dueDate: fee.dueDate,
                installmentRowSpan: index === 0 ? feesCount + 1 : 0,
                isFirstInGroup: index === 0
            });
        });

        const total = group.fees.reduce((acc, fee) => ({
            termWiseActualValue: acc.termWiseActualValue + fee.termWiseActualValue,
            concession: acc.concession + fee.concession,
            netAmount: acc.netAmount + fee.netAmount,
            amountPaid: acc.amountPaid + fee.amountPaid,
            dueAmount: acc.dueAmount + fee.dueAmount
        }), { termWiseActualValue: 0, concession: 0, netAmount: 0, amountPaid: 0, dueAmount: 0 });

        tableData.push({
            sNo: '',
            paymentTerm: group.paymentTerm,
            feeType: 'Total :',
            termWiseActualValue: total.termWiseActualValue,
            concession: total.concession,
            netAmount: total.netAmount,
            amountPaid: total.amountPaid,
            dueAmount: total.dueAmount,
            dueDate: '',
            isTotal: true
        });
    });

    const grandTotal = tableData
        .filter(row => !row.isTotal && !row.isCumulative)
        .reduce((acc, row) => ({
            termWiseActualValue: acc.termWiseActualValue + row.termWiseActualValue,
            concession: acc.concession + row.concession,
            netAmount: acc.netAmount + row.netAmount,
            amountPaid: acc.amountPaid + row.amountPaid,
            dueAmount: acc.dueAmount + row.dueAmount
        }), { termWiseActualValue: 0, concession: 0, netAmount: 0, amountPaid: 0, dueAmount: 0 });

    const feeTypeBodyTemplate = (rowData) => {
        if (rowData.isTotal) {
            return (
                <span style={{
                    fontFamily: 'InterSemiBold',
                    color: '#076EFF',
                    fontSize: '0.9375rem',
                    fontWeight: 600
                }}>
                    Total :
                </span>
            );
        }
        return (
            <span style={{
                fontFamily: 'InterMedium',
                color: '#374151',
                fontSize: '0.9375rem'
            }}>
                {rowData.feeType}
            </span>
        );
    };

    const amountBodyTemplate = (field) => (rowData) => {
        const value = rowData[field];
        if (rowData.isTotal) {
            return (
                <span style={{
                    fontFamily: 'InterSemiBold',
                    color: '#076EFF',
                    fontSize: '0.9375rem',
                    fontWeight: 600
                }}>
                    {value.toFixed(2)}
                </span>
            );
        }

        return (
            <span style={{
                fontFamily: 'InterMedium',
                color: '#1F2937',
                fontSize: '0.9375rem'
            }}>
                {value.toFixed(2)}
            </span>
        );
    };

    const dueDateBodyTemplate = (rowData) => {
        if (rowData.isTotal) return null;

        let dateString = '';
        if (rowData.dueDate) {
            const date = new Date(rowData.dueDate);
            if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                dateString = `${day}/${month}/${year}`;
            } else {
                dateString = rowData.dueDate;
            }
        }

        return (
            <span style={{
                fontFamily: 'InterMedium',
                color: '#1F2937',
                fontSize: '0.9375rem'
            }}>
                {dateString}
            </span>
        );
    };

    const rowClassName = (rowData) => {
        if (rowData.isTotal) return 'installment-total-row';
        return 'installment-data-row';
    };

    return (
        <div className="tab-content feetype-wise-content">
            <FeeFilter activeFilter={feeFilter} onFilterChange={setFeeFilter} />

            <div className="fee-table-container card datatable-crud-demo "style={{ overflow: 'auto' }}>
                <DataTable
                    value={tableData}
                    className="fee-details-datatable"
                    rowGroupMode="rowspan"
                    groupRowsBy="paymentTerm"
                    stripedRows
                    rowClassName={rowClassName}
                    showGridlines
                    emptyMessage="No installment data available"
                >
                    <Column
                        field="paymentTerm"
                        header="INSTALLMENT"
                        body={(rowData) => rowData.isTotal ? '' : rowData.paymentTerm}
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="feeType"
                        header="FEE TYPE"
                        body={feeTypeBodyTemplate}
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        field="termWiseActualValue"
                        header="FEE AMOUNT"
                        body={(rowData) => amountBodyTemplate('termWiseActualValue')(rowData)}
                        style={{ minWidth: '140px' }}
                    />
                    <Column
                        field="concession"
                        header="CONCESSION"
                        body={(rowData) => amountBodyTemplate('concession')(rowData)}
                        style={{ minWidth: '140px' }}
                    />
                    <Column
                        field="netAmount"
                        header="NET AMOUNT"
                        body={(rowData) => amountBodyTemplate('netAmount')(rowData)}
                        style={{ minWidth: '140px' }}
                    />
                    <Column
                        field="amountPaid"
                        header="PAID AMOUNT"
                        body={(rowData) => amountBodyTemplate('amountPaid')(rowData)}
                        style={{ minWidth: '140px' }}
                    />
                    <Column
                        field="dueAmount"
                        header="BALANCE"
                        body={(rowData) => amountBodyTemplate('dueAmount')(rowData)}
                        style={{ minWidth: '140px' }}
                    />
                    <Column
                        field="dueDate"
                        header="DUE DATE"
                        body={dueDateBodyTemplate}
                        style={{ minWidth: '140px' }}
                    />
                </DataTable>

                {/* Grand Total Row */}
                <DataTable
                    value={[{
                        paymentTerm: '',
                        feeType: 'GRAND TOTAL :',
                        termWiseActualValue: grandTotal.termWiseActualValue,
                        concession: grandTotal.concession,
                        netAmount: grandTotal.netAmount,
                        amountPaid: grandTotal.amountPaid,
                        dueAmount: grandTotal.dueAmount,
                        dueDate: ''
                    }]}
                    className="installment-grand-totals-table"
                    showGridlines
                >
                    <Column body={() => ''} style={{ minWidth: '150px' }} />
                    <Column body={() => <strong style={{ color: '#076EFF' }}>GRAND TOTAL :</strong>} style={{ minWidth: '180px' }} />
                    <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.termWiseActualValue.toFixed(2)}</strong>} style={{ minWidth: '140px' }} />
                    <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.concession.toFixed(2)}</strong>} style={{ minWidth: '140px' }} />
                    <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.netAmount.toFixed(2)}</strong>} style={{ minWidth: '140px' }} />
                    <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.amountPaid.toFixed(2)}</strong>} style={{ minWidth: '140px' }} />
                    <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.dueAmount.toFixed(2)}</strong>} style={{ minWidth: '140px' }} />
                    <Column body={() => ''} style={{ minWidth: '140px' }} />
                </DataTable>
            </div>
        </div>
    );
};
